import { useProjectParams } from '@hooks/useProjectParams';
import { ControlledSelectField } from 'components/Form';
import { OtherManualSeasonDistribution } from 'components/OtherManualSeasonDistribution';
import type { ConstructionTypeData } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useUerContractSeasons } from 'pages/Project/Common/Contract/hooks/useUerContractSeasons';
import { seasonItems } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import type { IUpdateOtherCostCanEditPrice } from 'pages/Project/Common/Contract/OtherEdit/hooks/useUerUpdateContractOtherCostCanEdit';
import { UpdateOtherCostEditable } from 'pages/Project/Common/Contract/OtherEdit/hooks/useUerUpdateContractOtherCostCanEdit';
import { validateLimitCost } from 'pages/Project/Common/hooks/prepareLimitsData';
import type { IUerContractOtherCostLimitSpanSelectionData } from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { Checkbox, FieldGroup, Grid, GridItem } from 'uikit';

import cn from 'classnames';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormClearErrors, UseFormSetValue } from 'react-hook-form/dist/types/form';

import type { TConstructionTypesIndexes } from '../../pages/Project/Common/types';
import { ContractOtherCostLimitSpan } from '../ContractOtherCostLimitSpan/ContractOtherCostLimitSpan';

import Styles from './EditOtherForm.module.css';

interface IEditOtherLimits {
    control: Control<any>;
    selectedConstructionType: Record<number, boolean>;
    changeSelectedConstructionTypeHandler: (id: number, newState: boolean) => void;
    currentConstructionTypeId: number;
    constructionTypes?: ConstructionTypeData[];
    isTooltipHidden?: boolean;
    costRounding: number;
    clearErrors?: UseFormClearErrors<any>;
    setValue: UseFormSetValue<any>;
    spanSelectionData?: IUerContractOtherCostLimitSpanSelectionData;
    editConfig?: IUpdateOtherCostCanEditPrice;
}

export const EditOtherLimits = (props: IEditOtherLimits) => {
    const {
        control,
        selectedConstructionType,
        changeSelectedConstructionTypeHandler,
        currentConstructionTypeId,
        constructionTypes,
        isTooltipHidden,
        costRounding,
        clearErrors,
        setValue,
        spanSelectionData,
        editConfig,
    } = props;

    const { id: projectId, methodology, orderId } = useProjectParams();

    const { projectData } = useProjectData(projectId, false);

    const isMethodologyUER = methodology?.toUpperCase() === ProjectMethodologyEnum.Uer;
    const isMethodologyBIM = methodology?.toUpperCase() === ProjectMethodologyEnum.Bim;

    const constructionTypeCheckHandler = (params: { checked: boolean }, id: number) => {
        changeSelectedConstructionTypeHandler(id, params.checked);
    };

    const [contractSeasons] = useUerContractSeasons(projectData?.contractId, Boolean(orderId));

    const constructionTypesSortName = useMemo(
        () => (constructionTypes ? [...constructionTypes].sort((a, b) => a?.name?.localeCompare(b?.name)) : []),
        [constructionTypes],
    );

    const currentName = useWatch({ control, name: 'name' });

    const canEdit = useMemo(() => editConfig?.[UpdateOtherCostEditable.canEdit], [editConfig]);
    const canUseOtherSpans = useMemo(() => editConfig?.[UpdateOtherCostEditable.canUseOtherSpans], [editConfig]);

    return (
        <Grid>
            <GridItem
                className={cn({ [Styles.labelBlock]: isMethodologyUER, [Styles.bimLabelBlock]: isMethodologyBIM })}
            >
                <span>Вид строительства</span>
                <span
                    className={cn({ [Styles.priceLabel]: isMethodologyUER, [Styles.priceLabel_bim]: isMethodologyBIM })}
                >
                    Сумма, руб. без НДС
                </span>
                {isMethodologyUER && <span className={Styles.typeLabel}>Способ распределения</span>}
            </GridItem>
            {constructionTypesSortName?.map(({ id, name }) => {
                const constructionTypeEditConfig =
                    editConfig?.[UpdateOtherCostEditable.limits]?.[id as unknown as TConstructionTypesIndexes];

                const canEditConstructionType =
                    !constructionTypeEditConfig ||
                    !!constructionTypeEditConfig?.[UpdateOtherCostEditable.constructionType];
                const canExcludeConstructionType =
                    !constructionTypeEditConfig ||
                    !!constructionTypeEditConfig?.[UpdateOtherCostEditable.constructionTypeExclude];
                const canEditDistributionMethod =
                    !constructionTypeEditConfig ||
                    !!constructionTypeEditConfig?.[UpdateOtherCostEditable.distributionMethod];
                // TODO: в uer всегда перераспределение noLimitSpan={false} => используются флаги canEditLimit, canEditLimitSpan
                // const canEditCost = !!constructionTypeEditConfig?.[UerUpdateContractOtherCostEditable.cost];
                const canEditLimit =
                    !constructionTypeEditConfig || !!constructionTypeEditConfig?.[UpdateOtherCostEditable.limit];
                const canEditLImitSpan =
                    !constructionTypeEditConfig || !!constructionTypeEditConfig?.[UpdateOtherCostEditable.limitSpan];

                const defaultConstruction = id === currentConstructionTypeId;
                const isChecked = selectedConstructionType[id] ?? false;
                const isCheckDisabled =
                    defaultConstruction ||
                    !canEdit ||
                    !canEditConstructionType ||
                    (isChecked && !canExcludeConstructionType);
                const isDistributionMethodDisabled = !isChecked || !canEdit || !canEditDistributionMethod;
                const isLimitEditDisabled = !isChecked || !canEdit || !canEditLimit;
                const isLimitSpanEditDisabled = !isChecked || !canEdit || !canEditLImitSpan;

                const rules = {
                    validate: validateLimitCost({
                        isSelected: !isDistributionMethodDisabled,
                    }),
                };

                const label = (
                    <Checkbox
                        id={String(id)}
                        onChange={(params) => constructionTypeCheckHandler(params, id)}
                        checked={isChecked}
                        disabled={isCheckDisabled}
                        style={{ marginRight: 10, marginTop: 5, cursor: 'pointer' }}
                        label={name}
                    />
                );

                return (
                    <GridItem key={id} className={Styles.marginBlock}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div className={Styles.wrapperContractLimitSpan} style={{ marginRight: '16px' }}>
                                <ContractOtherCostLimitSpan
                                    label={label}
                                    control={control}
                                    costRounding={costRounding}
                                    defaultConstruction={defaultConstruction}
                                    constructionTypeId={id}
                                    constructionTypeName={name}
                                    isDisabled={isLimitEditDisabled}
                                    isLimitSpanEditDisabled={isLimitSpanEditDisabled}
                                    isChecked={isChecked}
                                    isTooltipHidden={!!isTooltipHidden}
                                    setValue={setValue}
                                    noLimitSpan={!canUseOtherSpans}
                                    name={currentName}
                                    spanSelectionData={spanSelectionData}
                                    inputClassName={Styles.styleInputCostLimit}
                                />
                            </div>
                            <FieldGroup size="s" className={Styles.seasonSelect}>
                                <ControlledSelectField
                                    name={`method.${id}`}
                                    control={control}
                                    items={seasonItems}
                                    className={Styles.seasonSelect}
                                    required={defaultConstruction}
                                    rules={rules}
                                    disabled={isDistributionMethodDisabled}
                                    clearOnDisabled={!isChecked}
                                    isTooltipHidden={isTooltipHidden}
                                    placeholder={isDistributionMethodDisabled ? '' : 'Выберите'}
                                    tooltipDirection="downCenter"
                                />
                                <OtherManualSeasonDistribution
                                    control={control}
                                    constructionTypeName={name}
                                    constructionTypeId={id}
                                    costRounding={costRounding ?? 0}
                                    isDisabled={isDistributionMethodDisabled}
                                    clearErrors={clearErrors}
                                    seasons={contractSeasons}
                                />
                            </FieldGroup>
                        </div>
                    </GridItem>
                );
            })}
        </Grid>
    );
};

EditOtherLimits.displayName = 'EditOtherLimits';
